import type { BudgetSummary, Cadence, Classification } from '@heivol-finance/calc';

/** Thin JSON fetch layer for the module APIs. All calls are same-origin, cookie-authed. */
async function http<T>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin' };
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`${method} ${url} → ${res.status}`);
  return (await res.json()) as T;
}
export const api = {
  get: <T>(u: string) => http<T>('GET', u),
  post: <T>(u: string, b?: unknown) => http<T>('POST', u, b),
  patch: <T>(u: string, b?: unknown) => http<T>('PATCH', u, b),
  put: <T>(u: string, b?: unknown) => http<T>('PUT', u, b),
  del: <T>(u: string) => http<T>('DELETE', u),
};

// ── Budget (sheet Common) ──────────────────────────────────────────────────────
export interface Category {
  id: string;
  name: string;
  kind: 'income' | 'expense';
  parent_id: string | null;
}
export interface IncomeSource {
  id: string;
  name: string;
  amount_ore: number;
  cadence: Cadence;
  category_id: string | null;
}
export interface ExpenseItem {
  id: string;
  name: string;
  category_id: string | null;
  planned_ore: number;
  cadence: Cadence;
  is_investment_contribution: boolean;
}
export interface BudgetData {
  categories: Category[];
  incomes: IncomeSource[];
  expenses: ExpenseItem[];
  summary: BudgetSummary;
}
export const budgetApi = {
  load: () => api.get<BudgetData>('/api/budget'),
  addIncome: (b: Partial<IncomeSource>) => api.post<IncomeSource>('/api/budget/income', b),
  updateIncome: (id: string, b: Partial<IncomeSource>) => api.patch<IncomeSource>(`/api/budget/income/${id}`, b),
  deleteIncome: (id: string) => api.del(`/api/budget/income/${id}`),
  addExpense: (b: Partial<ExpenseItem>) => api.post<ExpenseItem>('/api/budget/expense', b),
  updateExpense: (id: string, b: Partial<ExpenseItem>) => api.patch<ExpenseItem>(`/api/budget/expense/${id}`, b),
  deleteExpense: (id: string) => api.del(`/api/budget/expense/${id}`),
};

// ── Net worth (sheet Accumulated) ──────────────────────────────────────────────
export interface LedgerEventRow {
  id: string;
  dated: string;
  label: string;
  amount_ore: number;
  classification: Classification;
  linked_entity: string | null;
}
export interface NetWorthSettings {
  openingNetWorthOre: number;
  horizonMonths: number;
  startYear: number;
  startMonth: number;
}
export interface NetWorthData {
  events: LedgerEventRow[];
  settings: NetWorthSettings;
}
export const netWorthApi = {
  load: () => api.get<NetWorthData>('/api/networth'),
  saveSettings: (s: NetWorthSettings) => api.put<NetWorthSettings>('/api/networth/settings', s),
  addEvent: (b: Partial<LedgerEventRow>) => api.post<LedgerEventRow>('/api/networth/event', b),
  updateEvent: (id: string, b: Partial<LedgerEventRow>) => api.patch<LedgerEventRow>(`/api/networth/event/${id}`, b),
  deleteEvent: (id: string) => api.del(`/api/networth/event/${id}`),
};

// ── Projection / FIRE (sheet Goals) ────────────────────────────────────────────
export interface BucketRow {
  id: string;
  name: string;
  opening_value_ore: number;
  annual_contribution_ore: number;
  return_schedule: number[];
  tax_treatment: 'free' | 'pension';
}
export interface ScenarioRow {
  id?: string;
  name: string;
  inflation: number;
  contribution_growth: number;
  target_age: number;
  withdrawal_rate: number;
  real_or_nominal: 'real' | 'nominal';
  start_age: number;
  pension_opening_ore: number;
  pension_annual_add_ore: number;
  /** Single liquidity target (øre) stashed in return_overrides; net worth crossing it → "reached". */
  liquidity_goal_ore: number;
}
export interface GoalRow {
  id: string;
  name: string;
  target_amount_ore: number;
  due_date: string | null;
  kind: 'financial' | 'career' | 'life';
  progress_ore: number;
}
/** A dedicated retirement-spending line — mirrors an expense_item's cadence + planned amount. */
export interface RetirementExpenseRow {
  id: string;
  name: string;
  planned_ore: number;
  cadence: Cadence;
}
export interface ProjectionData {
  buckets: BucketRow[];
  scenario: ScenarioRow;
  goals: GoalRow[];
  retirementExpenses: RetirementExpenseRow[];
}
export const projectionApi = {
  load: () => api.get<ProjectionData>('/api/projection'),
  addBucket: (b: Partial<BucketRow>) => api.post<BucketRow>('/api/projection/bucket', b),
  updateBucket: (id: string, b: Partial<BucketRow>) => api.patch<BucketRow>(`/api/projection/bucket/${id}`, b),
  deleteBucket: (id: string) => api.del(`/api/projection/bucket/${id}`),
  saveScenario: (s: Partial<ScenarioRow>) => api.put<ScenarioRow>('/api/projection/scenario', s),
  addGoal: (b: Partial<GoalRow>) => api.post<GoalRow>('/api/projection/goal', b),
  updateGoal: (id: string, b: Partial<GoalRow>) => api.patch<GoalRow>(`/api/projection/goal/${id}`, b),
  deleteGoal: (id: string) => api.del(`/api/projection/goal/${id}`),
  addRetirementExpense: (b: Partial<RetirementExpenseRow>) =>
    api.post<RetirementExpenseRow>('/api/projection/retirement-expense', b),
  updateRetirementExpense: (id: string, b: Partial<RetirementExpenseRow>) =>
    api.patch<RetirementExpenseRow>(`/api/projection/retirement-expense/${id}`, b),
  deleteRetirementExpense: (id: string) => api.del(`/api/projection/retirement-expense/${id}`),
};

// ── Loans (sheet Loans) ──────────────────────────────────────────────────────
export type LoanKind = 'annuity' | 'serial' | 'interest_only' | 'informal';
export interface LoanRow {
  id: string;
  name: string;
  lender: string | null;
  type: LoanKind;
  principal_ore: number;
  rate: number; // annual nominal rate (fraction, e.g. 0.03)
  term_months: number | null;
  start_date: string | null;
}
export interface LoanLedgerEntry {
  id: string;
  loan_id: string;
  dated: string;
  amount_ore: number; // signed draw (−) / repayment (+)
  running_balance_ore: number;
}
export interface LoansData {
  loans: LoanRow[];
  entries: LoanLedgerEntry[];
}
export const loansApi = {
  load: () => api.get<LoansData>('/api/loans'),
  addLoan: (b: Partial<LoanRow>) => api.post<LoanRow>('/api/loans', b),
  updateLoan: (id: string, b: Partial<LoanRow>) => api.patch<LoanRow>(`/api/loans/${id}`, b),
  deleteLoan: (id: string) => api.del(`/api/loans/${id}`),
  addEntry: (loanId: string, b: Partial<LoanLedgerEntry>) =>
    api.post<LoanLedgerEntry[]>(`/api/loans/${loanId}/entry`, b),
  updateEntry: (loanId: string, entryId: string, b: Partial<LoanLedgerEntry>) =>
    api.patch<LoanLedgerEntry[]>(`/api/loans/${loanId}/entry/${entryId}`, b),
  deleteEntry: (loanId: string, entryId: string) =>
    api.del<LoanLedgerEntry[]>(`/api/loans/${loanId}/entry/${entryId}`),
};

// ── Assets (sheets House / Charger / Græstoftevej) ─────────────────────────────
export type AssetType = 'property' | 'vehicle' | 'collectible' | 'other';
export type AssetCostStatus = 'planned' | 'verified' | 'paid';
export interface AssetRow {
  id: string;
  name: string;
  type: AssetType;
  purchase_price_ore: number;
  current_value_ore: number;
  appreciation_rate: number; // assumed annual appreciation (fraction, e.g. 0.02)
  linked_loan_id: string | null;
}
export interface AssetCostRow {
  id: string;
  asset_id: string;
  dated: string | null;
  amount_ore: number;
  status: AssetCostStatus;
  note: string | null;
}
export interface AssetsData {
  assets: AssetRow[];
  costs: AssetCostRow[];
}
export const assetsApi = {
  load: () => api.get<AssetsData>('/api/assets'),
  addAsset: (b: Partial<AssetRow>) => api.post<AssetRow>('/api/assets', b),
  updateAsset: (id: string, b: Partial<AssetRow>) => api.patch<AssetRow>(`/api/assets/${id}`, b),
  deleteAsset: (id: string) => api.del(`/api/assets/${id}`),
  addCost: (assetId: string, b: Partial<AssetCostRow>) => api.post<AssetCostRow>(`/api/assets/${assetId}/cost`, b),
  updateCost: (assetId: string, costId: string, b: Partial<AssetCostRow>) =>
    api.patch<AssetCostRow>(`/api/assets/${assetId}/cost/${costId}`, b),
  deleteCost: (assetId: string, costId: string) => api.del(`/api/assets/${assetId}/cost/${costId}`),
};

// ── Travel (sheet Travel) ──────────────────────────────────────────────────────
export type TripStatus = 'idea' | 'booked' | 'paid';
export interface TripRow {
  id: string;
  name: string;
  month: string; // first-of-month 'YYYY-MM-DD'
  status: TripStatus;
  posted: boolean; // whether a consumptive ledger_event is booked into the Formue curve
}
export interface TripCostRow {
  id: string;
  trip_id: string;
  category: string;
  amount_ore: number;
}
export interface TravelData {
  trips: TripRow[];
  costs: TripCostRow[];
}
export const travelApi = {
  load: () => api.get<TravelData>('/api/travel'),
  addTrip: (b: Partial<TripRow>) => api.post<TripRow>('/api/travel', b),
  updateTrip: (id: string, b: Partial<TripRow>) => api.patch<TripRow>(`/api/travel/${id}`, b),
  deleteTrip: (id: string) => api.del(`/api/travel/${id}`),
  postToNetWorth: (id: string) => api.post<{ posted: true; amount_ore: number }>(`/api/travel/${id}/post`),
  unpostFromNetWorth: (id: string) => api.del<{ posted: false }>(`/api/travel/${id}/post`),
  addCost: (tripId: string, b: Partial<TripCostRow>) => api.post<TripCostRow>(`/api/travel/${tripId}/cost`, b),
  updateCost: (tripId: string, costId: string, b: Partial<TripCostRow>) =>
    api.patch<TripCostRow>(`/api/travel/${tripId}/cost/${costId}`, b),
  deleteCost: (tripId: string, costId: string) => api.del(`/api/travel/${tripId}/cost/${costId}`),
};

// ── Receivables (sheet Lenders) ────────────────────────────────────────────────
export interface ReceivableRow {
  id: string;
  debtor: string;
  item: string | null;
  amount_ore: number;
}
export interface ReceivablesData {
  receivables: ReceivableRow[];
}
export const receivablesApi = {
  load: () => api.get<ReceivablesData>('/api/receivables'),
  add: (b: Partial<ReceivableRow>) => api.post<ReceivableRow>('/api/receivables', b),
  update: (id: string, b: Partial<ReceivableRow>) => api.patch<ReceivableRow>(`/api/receivables/${id}`, b),
  remove: (id: string) => api.del(`/api/receivables/${id}`),
};

// ── Settings (sheet Settings) ──────────────────────────────────────────────────
export interface SettingsRules {
  debtFactorSoft: number;
  debtFactorHard: number;
  ltvHome: number;
  ltvHoliday: number;
  ltvCommercial: number;
  livingCostAdultOre: number;
  livingCostChildOre: number;
  withdrawalRate: number;
  inflation: number;
}
export interface SettingsData {
  currency: string;
  locale: string;
  rules: SettingsRules;
}
export const settingsApi = {
  load: () => api.get<SettingsData>('/api/settings'),
  saveRules: (r: SettingsRules) => api.put<SettingsData>('/api/settings', r),
  /** Persist the display currency (server derives locale) while keeping the current rules intact. */
  saveCurrency: (currency: string, rules: SettingsRules) =>
    api.put<SettingsData>('/api/settings', { currency, ...rules }),
};

export const seedApi = {
  run: () => api.post<{ seeded: boolean; reason?: string }>('/api/seed'),
};

// ── Saxo Bank (read-only holdings + self-sustaining server-side token) ──────────
// The server holds the OAuth token; the browser only reads health/holdings and *triggers*
// (re)connect / refresh. Connect returns an authorizeUrl the browser navigates to — the user
// logs in at Saxo, which redirects back to /api/saxo/callback (server-side seed). No token
// ever reaches the SPA. See heivol-finance/saxo-integration-spec §7.
export interface SaxoHealth {
  connected: boolean;
  status: 'active' | 'stale' | 'revoked' | null;
  accessExpiresAt: string | null;
  refreshExpiresAt: string | null;
  lastRefreshAt: string | null;
}
/** Loose snapshot — Saxo returns rich nested objects; the UI reads a few summary fields. */
export interface SaxoSnapshot {
  fetchedAt: string;
  client: unknown;
  accounts: unknown[];
  balances: { TotalValue?: number; CashBalance?: number; Currency?: string } & Record<string, unknown>;
  positions: unknown[];
  netPositions: unknown[];
  exposure: { instruments: unknown[]; currency: unknown[]; fxSpot: unknown[] };
}
export const saxoApi = {
  status: () => api.get<SaxoHealth>('/api/saxo/status'),
  connect: () => api.post<{ authorizeUrl: string; state: string }>('/api/saxo/connect'),
  refresh: () => api.post<SaxoHealth>('/api/saxo/refresh'),
  holdings: () => api.get<SaxoSnapshot>('/api/saxo/holdings'),
};
